import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';

import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { localize } from 'common/utils';
import Loading from 'core/Loading';
import PageLoadError from 'core/PageLoadError';

import { SpinnerContainer } from '../Experience.styled';
import { ExperienceModuleContextProps, ExperienceModuleContextProvider } from '../context';
import { useActiveNavigation } from '../hooks/useActiveNavigation';
import { useSidebar } from '../hooks/useSidebar';
import { useVenue } from '../hooks/useVenue';
import { IndexPage } from '../pages/IndexPage';
import { ExperienceEntityTree, ExperienceEntity } from '../types';
import { getInitials } from '../utils';

const ContextAwareVenueExperience = ({
  experienceEntity,
  experienceEntityTree,
}: {
  experienceEntity: ExperienceEntity;
  experienceEntityTree: ExperienceEntityTree;
}) => {
  // Naming
  const { locale } = useIntl();
  const venue = experienceEntity.experienceEntityObject as Venue;
  const venueName = localize(venue.name, locale);

  // Navigation
  const { activeNavigation, setActiveNavigation } = useActiveNavigation();
  const { isSidebarOpen, setIsSidebarOpen } = useSidebar();
  const [selectedExperienceEntity, setSelectedExperienceEntity] = useState<ExperienceEntity>(experienceEntity);

  // Venue
  const [selectedVenueExperienceEntity, setSelectedVenueExperienceEntity] =
    useState<ExperienceEntity>(experienceEntity);

  const contextValue: ExperienceModuleContextProps = useMemo(
    () => ({
      experienceEntity,
      experienceEntityTree,
      country: venue.country,
      currency: venue.currency,
      timezone: venue.timezone,
      name: venueName,
      address: venue.address,
      city: venue.city,
      image: venue.listimage ?? '',
      initials: getInitials(venueName),
      isWoltPlus: venue.is_wolt_plus,
      activeNavigation,
      setActiveNavigation,
      isSidebarOpen,
      setIsSidebarOpen,
      selectedExperienceEntity,
      setSelectedExperienceEntity,
      selectedVenueExperienceEntity,
      setSelectedVenueExperienceEntity,
    }),
    [
      venue,
      venueName,
      activeNavigation,
      isSidebarOpen,
      selectedExperienceEntity,
      selectedVenueExperienceEntity,
      setActiveNavigation,
      setIsSidebarOpen,
      experienceEntity,
      experienceEntityTree,
      setSelectedExperienceEntity,
      setSelectedVenueExperienceEntity,
    ],
  );

  return (
    <ExperienceModuleContextProvider value={contextValue}>
      <Helmet>
        <title>{venueName}</title>
        <meta name="description" content={venueName} />
      </Helmet>
      <IndexPage />
    </ExperienceModuleContextProvider>
  );
};

export const VenueExperience = ({ experienceEntityId }: { experienceEntityId: string }) => {
  const { experienceEntity, experienceEntityTree, error, isLoading } = useVenue(experienceEntityId);

  return (
    <>
      {/* todo @navjotahuja92 figure out how to not override whole page for loading instead reflect that in sidebar */}
      {isLoading && (
        <SpinnerContainer>
          <Loading />
        </SpinnerContainer>
      )}
      {!isLoading && experienceEntity && experienceEntityTree && (
        <ContextAwareVenueExperience experienceEntity={experienceEntity} experienceEntityTree={experienceEntityTree} />
      )}
      {!isLoading && error && <PageLoadError />}
    </>
  );
};
